/**
 * Get the Look article section — swatch swap, single/bulk add-to-cart, step video play.
 * Cart sync mirrors section-looks-post.js / section-related-products-v2.js (no webpack cartModel).
 */
(function () {
  const SELECTORS = {
    section: '.js-articleLook',
    product: '.js-articleLookProduct',
    productImage: '.js-articleLookProductImage',
    form: '.js-articleLookProductForm',
    swatch: '.js-articleLookProductSwatch',
    variant: '.js-articleLookProductVariant',
    formId: '.js-articleLookProductFormId',
    addAll: '.js-articleLookAdd',
    mediaVideo: '.js-mediaVideo',
  };

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getLineItemProperties(productTitle) {
    const props = { _product: productTitle };
    const settings = window.settings || {};
    if (
      settings.enable_test_promotion &&
      settings.test_promotion_property &&
      settings.test_promotion_property_value
    ) {
      props[settings.test_promotion_property] = settings.test_promotion_property_value;
    }
    return props;
  }

  function syncLiquidAjaxCart() {
    return new Promise((resolve) => {
      if (window.liquidAjaxCart && typeof window.liquidAjaxCart.update === 'function') {
        window.liquidAjaxCart.update({}, { lastCallback: () => resolve(true) });
        return;
      }
      resolve(false);
    });
  }

  function refreshCartSections() {
    const jobs = Array.from(document.querySelectorAll('[data-ajax-cart-section]')).map((cartEl) => {
      const sectionEl = cartEl.closest('[id^="shopify-section-"]');
      if (!sectionEl) return Promise.resolve();
      const sid = sectionEl.id.replace('shopify-section-', '');
      return fetch(`${window.location.pathname}?sections=${sid}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data[sid]) return;
          const doc = new DOMParser().parseFromString(data[sid], 'text/html');
          const fresh = doc.querySelector('[data-ajax-cart-section]');
          if (fresh) cartEl.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    });
    return Promise.all(jobs);
  }

  function syncCartCountBinders() {
    return fetch(`${cartRoot()}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const count = cart.item_count ?? 0;
        document.querySelectorAll('[data-ajax-cart-bind="item_count"]').forEach((el) => {
          el.textContent = String(count);
          el.classList.toggle('hidec', count === 0);
          el.classList.toggle('showc', count > 0);
        });
      })
      .catch(() => {});
  }

  function refreshCartAndOpen() {
    return syncLiquidAjaxCart()
      .then((lacSynced) => {
        if (!lacSynced) return syncCartCountBinders();
      })
      .then(refreshCartSections)
      .then(() => {
        window.dispatchEvent(new CustomEvent('cart-open'));
      });
  }

  function addItems(items) {
    return fetch(`${cartRoot()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    }).then((res) => {
      if (!res.ok) throw new Error('Add to cart failed');
      return res.json();
    });
  }

  function getVariantData(elSwatch, elForm) {
    const { name, value } = elSwatch;
    const variantData = JSON.parse(elForm.dataset.variants || '[]');
    return variantData.find((variant) => variant[name] === value);
  }

  function updateAddButtonState(elSwatch) {
    const elForm = elSwatch.closest(SELECTORS.form);
    if (!elForm) return;
    const isUnavailable = elSwatch.closest('.product__form-swatch--inner')?.classList.contains('is-unavailable');
    const elAddButton = elForm.querySelector('.article__look-product--button');
    if (!elAddButton) return;
    if (isUnavailable) {
      elAddButton.setAttribute('disabled', 'disabled');
      elAddButton.style.opacity = '0.5';
      elAddButton.style.cursor = 'not-allowed';
    } else {
      elAddButton.removeAttribute('disabled');
      elAddButton.style.opacity = '1';
      elAddButton.style.cursor = 'pointer';
    }
  }

  /** When the default variant has no rendered swatch (multi-option list), sync ATC from variant JSON. */
  function initFormButtonState(elForm) {
    const checked = elForm.querySelector(`${SELECTORS.swatch} input:checked`);
    if (checked) {
      updateAddButtonState(checked);
      return;
    }

    const elId = elForm.querySelector(SELECTORS.formId);
    const elAddButton = elForm.querySelector('.article__look-product--button');
    if (!elId?.value || !elAddButton) return;

    const variants = JSON.parse(elForm.dataset.variants || '[]');
    const variant = variants.find((v) => String(v.id) === String(elId.value));
    if (!variant) return;

    if (!variant.available) {
      elAddButton.setAttribute('disabled', 'disabled');
      elAddButton.style.opacity = '0.5';
      elAddButton.style.cursor = 'not-allowed';
    } else {
      elAddButton.removeAttribute('disabled');
      elAddButton.style.opacity = '1';
      elAddButton.style.cursor = 'pointer';
    }
  }

  function setCurrentVariant(ev) {
    const elSwatch = ev.target;
    if (!elSwatch || elSwatch.type !== 'radio') return;

    const elForm = ev.currentTarget;
    const elProduct = elForm.closest(SELECTORS.product);
    const elImage = elProduct?.querySelector(SELECTORS.productImage);
    const elId = elForm.querySelector(SELECTORS.formId);
    const elVariant = elForm.querySelector(SELECTORS.variant);
    const variantWithValue = getVariantData(elSwatch, elForm);

    if (variantWithValue && elImage?.querySelector('img')) {
      const img = elImage.querySelector('img');
      if (variantWithValue.featured_image) {
        img.src = variantWithValue.featured_image.src;
        img.srcset = variantWithValue.featured_image.src;
      }
    }

    if (variantWithValue && elId) {
      elId.value = variantWithValue.id;
    }

    if (variantWithValue && elVariant) {
      elVariant.textContent = variantWithValue.title;
    }

    updateAddButtonState(elSwatch);
  }

  function updateProductImage(ev) {
    const elSwatch = ev.currentTarget.querySelector('input');
    if (!elSwatch) return;

    const elForm = elSwatch.closest(SELECTORS.form);
    const elProduct = elForm?.closest(SELECTORS.product);
    const elImage = elProduct?.querySelector(SELECTORS.productImage);
    const variantWithValue = getVariantData(elSwatch, elForm);

    if (variantWithValue?.featured_image && elImage?.querySelector('img')) {
      const img = elImage.querySelector('img');
      img.src = variantWithValue.featured_image.src;
      img.srcset = variantWithValue.featured_image.src;
    }
  }

  function onAddProduct(ev) {
    ev.preventDefault();
    const elForm = ev.currentTarget;
    const elId = elForm.querySelector(SELECTORS.formId);
    const elButton = elForm.querySelector('.article__look-product--button');
    if (!elId?.value || elButton?.disabled) return;

    if (elButton) {
      elButton.disabled = true;
    }

    addItems([
      {
        id: parseInt(elId.value, 10),
        quantity: 1,
        properties: getLineItemProperties(elId.dataset.product),
      },
    ])
      .then(refreshCartAndOpen)
      .catch((err) => console.error('article-look add:', err))
      .finally(() => {
        if (elButton) elButton.disabled = false;
      });
  }

  function onAddAllProducts(ev) {
    const elButton = ev.currentTarget;
    const elSection = elButton.closest(SELECTORS.section);
    if (!elSection) return;

    const items = [];
    elSection.querySelectorAll(SELECTORS.product).forEach((elProduct) => {
      const elId = elProduct.querySelector(SELECTORS.formId);
      if (!elId?.value) return;
      items.push({
        id: parseInt(elId.value, 10),
        quantity: 1,
        properties: getLineItemProperties(elId.dataset.product),
      });
    });

    if (!items.length) return;

    elButton.disabled = true;
    addItems(items)
      .then(refreshCartAndOpen)
      .catch((err) => console.error('article-look bulk add:', err))
      .finally(() => {
        elButton.disabled = false;
      });
  }

  function togglePlayVideo(ev) {
    ev.preventDefault();
    const elContainer = ev.currentTarget;
    const elVideo = elContainer.querySelector('video');
    if (!elVideo || elContainer.dataset.autoplay === 'true') return;

    if (elVideo.paused) {
      elContainer.classList.add('playing');
      elVideo.play().catch(() => {});
    } else {
      elContainer.classList.remove('playing');
      elVideo.pause();
    }
  }

  function initSection(elSection) {
    elSection.querySelectorAll(SELECTORS.form).forEach((elForm) => {
      elForm.addEventListener('change', setCurrentVariant);
      elForm.addEventListener('submit', onAddProduct);
      initFormButtonState(elForm);
    });

    elSection.querySelectorAll(SELECTORS.swatch).forEach((el) => {
      el.addEventListener('mouseover', updateProductImage);
    });

    elSection.querySelectorAll(SELECTORS.addAll).forEach((el) => {
      el.addEventListener('click', onAddAllProducts);
    });

    elSection.querySelectorAll(SELECTORS.mediaVideo).forEach((el) => {
      el.addEventListener('click', togglePlayVideo);
    });
  }

  function init() {
    document.querySelectorAll(SELECTORS.section).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
